import axios from 'axios';
import { setAuthState, setAgentRefreshedCookie, setTempState, getAuthState } from '../../assets/scripts/login-util';
import { sleep, displayErrorSwal } from './common';
import Swal from 'sweetalert2';
import { version } from '../../version.json'

const tokenErrors = ['bearer undefined', "bearer false"];
const errorLogs = ['Session Expired', 'Authentication failed', 'User not found', 'Email not verified', 'User not authorized', 'Authetication Error',
                    'User not authenticated', 'Team is not active'
                  ];

/**
 * Task 6: shows the "subscription not active" notice at most once per
 * cooldown window, no matter how many requests 403 in a burst (e.g. a
 * dashboard page mounting several widgets, each making its own call, all
 * blocked identically). Never navigates on its own - the button inside
 * the swal is the only way this causes a page change, and only on a
 * click. This is what actually stops the reload loop; the swal alone
 * firing repeatedly in a tight burst is harmless UI noise, but an
 * automatic `window.location.href` on every occurrence is not.
 */
const SUBSCRIPTION_NOTICE_COOLDOWN_MS = 60000;
let lastSubscriptionNoticeAt = 0;

const notifySubscriptionExpired = () => {
  const now = Date.now();
  if (now - lastSubscriptionNoticeAt < SUBSCRIPTION_NOTICE_COOLDOWN_MS) return;
  lastSubscriptionNoticeAt = now;

  const authUser = getAuthState('user');
  const isOwner  = authUser && Number(authUser.categoryId) === 1;

  Swal.fire({
    icon               : 'info',
    titleText          : 'Subscription inactive',
    text               : isOwner
      ? 'Your subscription is not active. Visit Billing to renew.'
      : 'Your subscription is not active. Ask your account owner to renew it.',
    buttonsStyling     : false,
    confirmButtonClass : 'btn btn-brand',
    showCancelButton   : isOwner,
    confirmButtonText  : isOwner ? 'Go to Billing' : 'OK',
    cancelButtonText   : 'Dismiss',
    cancelButtonClass  : 'btn btn-secondary',
  }).then((result) => {
    if (isOwner && result.isConfirmed) {
      window.location.href = '/billing/overview';
    }
  });
};
const getErrorMessage = (err) => {
  if(err.response && err.response.data){ 
    let errMsg =  err.response.data.message;
    if(errorLogs.includes(errMsg)){
      displayErrorSwal(errMsg);
      window.open('/', '_self');
      setAuthState();
      setTempState();
    }
    // Task 6: once ENTITLEMENTS_ENABLED=true, 'Subscription Expired' stops
    // firing for a normal lapsed paid subscription - EXPIRED resolves
    // Free-plan access instead of being denied (see
    // ibbackend/services/entitlementService.js decide()). Until that flag
    // is flipped, though, the OLD gate (enforceLegacyFlags, still the
    // default) is what actually runs, and it keeps blocking every request
    // for a tenant whose TempSubscription has expired exactly as it always
    // has - unrelated to whether entitlements are on. Either way, this
    // handler must never assume the error is rare: NEVER force-navigate
    // here. A hard redirect on every 403 is what caused the reload loop -
    // every subsequent request 403s identically, the redirect reloads the
    // page, which immediately fires more requests that 403 again. Show an
    // informational notice, at most once per cooldown window, with a
    // button for the user to go to Billing themselves.
    if(errMsg == 'Subscription Expired'){
      notifySubscriptionExpired();
    }

    // ---- Local sentinels -------------------------------------------
    //
    // 'Minimum 3 license' (SUB_AGENCY not granted on a sub-agency URL)
    // and 'License reduced' (a LicenseReminder row exists) used to be
    // handled globally here too, via a cookie flag that opened a modal
    // from anywhere in the app. As of Task 6 they are local: one action
    // was refused, and the screen that asked (LicenseModal's own seat
    // precheck, header.js's direct getEntitlements() check driving
    // LicenseChangeReminder) already knows what to say about it without
    // a global broadcast. They travel through as ordinary errors, same
    // as the Phase 6/7 sentinels below always have.
    //
    //   SEAT_LIMIT_EXCEEDED   409  -> buy licences / deactivate someone
    //   FEATURE_NOT_ENTITLED  403  -> upgrade prompt
    //   NO_SUBSCRIPTION       409  -> ops problem, not the customer's
    //   BILLING_DISABLED      503  -> kill switch, contact support

    return errMsg;

  }else{
    return 'Unknown error';
  }
}

export const axiosPost = async (url, data, config = { headers:''}) => {
  if(config && tokenErrors.includes(config.headers.auth_token)) return sleep(2000);
  try {
    if (config)
      config.headers = {...config.headers, fe_version : version}
    const response  = await axios.post(url, data, config);

    if(response.headers['refresh_fe'] === 'true'){
      window.location.reload();
    }

    if(response.headers['refresh_cookie'] === 'true' && config){
      await setAgentRefreshedCookie(config);
    }
    return response.data;
  } catch (err) {
    const message = getErrorMessage(err);
    throw new Error(message || err.message || 'Unknown error');
  }
};

export const axiosGet = async (url, config = { headers:''}) => {
  if(config && tokenErrors.includes(config.headers.auth_token)) return sleep(2000);
  try {
    if (config)
      config.headers = {...config.headers, fe_version : version}
    const response = await axios.get(url, config);

    if(response.headers['refresh_fe'] === 'true'){
      window.location.reload();
    }

    if(response.headers['refresh_cookie'] === 'true' && config){
      await setAgentRefreshedCookie(config);
    }

    return response.data;
  } catch (err) {
     
    const message = getErrorMessage(err);
    throw new Error(message || err.message || 'Unknown error');
  }
};

export const axiosPatch = async (url, data, config) => {
  if(config && tokenErrors.includes(config.headers.auth_token)) return sleep(2000);
  try {
    // config.headers = {...config.headers, fe_version : version}
    const response = await axios.patch(url, data, config);

    // if(response.headers['refresh_fe'] === 'true'){
    //   window.location.reload();
    // }

    if(response.headers['refresh_cookie'] === 'true' && config ){
      await setAgentRefreshedCookie(config);
    }
    return response.data;
  } catch (err) {
    const message = getErrorMessage(err);
    throw new Error(message || err.message || 'Unknown error');
  }
};

export const axiosDelete = async (url, data, config) => {
  if(config && tokenErrors.includes(config.headers.auth_token)) return sleep(2000);
  try {
    const response = await axios.delete(url, data, config);

    // if(response.headers['refresh_fe'] === 'true'){
    //   window.location.reload();
    // }
    if(response.headers['refresh_cookie'] === 'true' && config ){
      await setAgentRefreshedCookie(config);
    }
    return response.data;
  } catch (err) {
    const message = getErrorMessage(err);
    throw new Error(message || err.message || 'Unknown error');
  }
};
